import { useState, useEffect } from 'react';
import { 
  Users, AlertTriangle, CheckCircle, Clock, Search, 
  Calendar, TrendingUp, BarChart3, Filter, ChevronLeft, ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Layout from '@/components/Layout';
import { api, toast } from '../App';
import ResourceBoard from './ResourceBoard';

const ResourceAllocation = ({ user }) => {
  const [resources, setResources] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateFilter, setDateFilter] = useState('this_week');
  const [activeTab, setActiveTab] = useState('summary');

  // Calendar state
  const [calendarData, setCalendarData] = useState([]);
  const [calendarLeaves, setCalendarLeaves] = useState({});  // user_id → [{start_date, end_date, status, is_half_day, ...}]
  const [calendarHolidays, setCalendarHolidays] = useState({});  // YYYY-MM-DD → holiday name
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  // Timezone-safe YYYY-MM-DD formatter. Using `.toISOString()` converts to UTC
  // and shifts the date by the timezone offset (e.g. India UTC+5:30 turns
  // 2026-06-01 local midnight into 2026-05-31T18:30Z → "2026-05-31"). We
  // therefore format from local fields directly.
  const ymd = (d) => {
    const dt = (d instanceof Date) ? d : new Date(d);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
  };

  // Initialize date range on mount and filter change
  useEffect(() => {
    const today = new Date();
    let start, end;
    
    if (dateFilter === 'this_week') {
      const day = today.getDay();
      start = new Date(today);
      start.setDate(today.getDate() - day + (day === 0 ? -6 : 1)); // Monday
      end = new Date(start);
      end.setDate(start.getDate() + 6); // Sunday
    } else if (dateFilter === 'this_month') {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    } else {
      const day = today.getDay();
      start = new Date(today);
      start.setDate(today.getDate() - day + (day === 0 ? -6 : 1));
      end = new Date(start);
      end.setDate(start.getDate() + 6);
    }
    
    setDateRange({ start: ymd(start), end: ymd(end) });
  }, [dateFilter]);

  // Load resources when date range changes OR Summary tab activates — keeps
  // the utilisation numbers fresh after task edits in Drag & Drop Board.
  useEffect(() => {
    if (activeTab !== 'summary') return;
    const loadResources = async () => {
      if (!dateRange.start || !dateRange.end) return;
      try {
        setLoading(true);
        const response = await api.get(`/planning/resource-utilization?start_date=${dateRange.start}&end_date=${dateRange.end}`);
        setResources(response.data.resources || []);
        setSummary(response.data.summary || {});
        setLoading(false);
      } catch (error) {
        console.error('Error fetching resources:', error);
        toast.error('Failed to load resource data');
        setLoading(false);
      }
    };
    loadResources();
  }, [dateRange, activeTab]);

  // Load calendar data when date range OR tab changes back to calendar.
  // Including `activeTab` ensures we refetch fresh data whenever the user
  // returns to the Calendar View after editing tasks in Drag & Drop Board.
  useEffect(() => {
    if (activeTab !== 'calendar') return;
    const loadCalendarData = async () => {
      if (!dateRange.start || !dateRange.end) return;
      try {
        const response = await api.get(`/planning/resource-calendar?start_date=${dateRange.start}&end_date=${dateRange.end}`);
        setCalendarData(response.data.calendar || []);
        setCalendarLeaves(response.data.leaves_by_user || {});
        setCalendarHolidays(response.data.holidays || {});
      } catch (error) {
        console.error('Error fetching calendar data:', error);
      }
    };
    loadCalendarData();
  }, [dateRange, activeTab]);

  // Generate dates array for the calendar
  const generateDates = () => {
    const dates = [];
    if (!dateRange.start || !dateRange.end) return dates;

    // Parse YYYY-MM-DD as LOCAL date — `new Date("YYYY-MM-DD")` parses as UTC
    // midnight which shifts the day by the timezone offset on any system west
    // of UTC.
    const [sy, sm, sd] = dateRange.start.split('-').map(Number);
    const [ey, em, ed] = dateRange.end.split('-').map(Number);
    const start = new Date(sy, sm - 1, sd);
    const end = new Date(ey, em - 1, ed);

    while (start <= end) {
      dates.push(new Date(start));
      start.setDate(start.getDate() + 1);
    }
    return dates;
  };

  const dates = generateDates();

  // Find a leave for (user_id, dateStr). Returns the most relevant leave (approved > pending) or null.
  const findLeaveForCell = (userId, dateStr) => {
    const list = calendarLeaves[userId] || [];
    const hits = list.filter(lv => (lv.start_date || '') <= dateStr && dateStr <= (lv.end_date || ''));
    if (hits.length === 0) return null;
    return hits.find(lv => lv.status === 'approved') || hits[0];
  };

  // Off-day classification: weekend (Sat/Sun) or company holiday. Holidays come from
  // /api/leave/holidays via the resource-calendar response; weekends are pure date logic.
  // Tasks are still allowed on off-days — this is a visual hint only.
  const classifyOffDay = (date, dateStr) => {
    const dow = date.getDay(); // 0=Sun, 6=Sat
    const holidayName = calendarHolidays[dateStr];
    if (holidayName) return { kind: 'holiday', label: holidayName };
    if (dow === 0 || dow === 6) return { kind: 'weekend', label: dow === 0 ? 'Sunday' : 'Saturday' };
    return null;
  };

  // Navigate weeks/months
  const navigatePeriod = (direction) => {
    // "This Month" must always span 1st → last day of a calendar month, never a
    // rolling 30-day window. Other filters slide by 7 days.
    // CRITICAL: `new Date("2026-05-01")` parses as UTC midnight — in any timezone
    // west of UTC this rolls back to April 30, breaking month arithmetic. Parse
    // the YYYY-MM-DD string explicitly to keep navigation timezone-agnostic.
    const [sy, sm] = (dateRange.start || '').split('-').map(Number);
    if (!sy || !sm) return;
    if (dateFilter === 'this_month') {
      const delta = direction === 'prev' ? -1 : 1;
      const newStart = new Date(sy, sm - 1 + delta, 1);
      const newEnd = new Date(newStart.getFullYear(), newStart.getMonth() + 1, 0);
      setDateRange({ start: ymd(newStart), end: ymd(newEnd) });
      return;
    }
    const [, , sd] = (dateRange.start || '').split('-').map(Number);
    const [ey, em, ed] = (dateRange.end || '').split('-').map(Number);
    const start = new Date(sy, sm - 1, sd);
    const end = new Date(ey, em - 1, ed);
    const days = 7;

    if (direction === 'prev') {
      start.setDate(start.getDate() - days);
      end.setDate(end.getDate() - days);
    } else {
      start.setDate(start.getDate() + days);
      end.setDate(end.getDate() + days);
    }

    setDateRange({ start: ymd(start), end: ymd(end) });
  };

  // Filter resources
  const filteredResources = resources.filter(resource => {
    const matchesSearch = resource.user_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || resource.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  // Get status badge with MD3 styling
  const getStatusBadge = (status) => {
    switch (status) {
      case 'overallocated':
        return <Badge className="bg-md3-error-container text-md3-on-error-container">Over-allocated</Badge>;
      case 'optimal':
        return <Badge className="bg-md3-tertiary-container text-md3-on-tertiary-container">Optimal</Badge>;
      case 'available':
        return <Badge className="bg-md3-primary-container text-md3-on-primary-container">Available</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  // Get utilization color
  const getUtilizationColor = (percentage) => {
    if (percentage > 100) return 'text-md3-error';
    if (percentage >= 85) return 'text-md3-tertiary';
    return 'text-md3-primary';
  };

  // Get cell background color based on allocation percentage
  const getCellBgColor = (percentage) => {
    if (!percentage || percentage === 0) return 'bg-md3-surface';
    if (percentage > 100) return 'bg-red-200';
    if (percentage >= 85) return 'bg-amber-200';
    if (percentage >= 50) return 'bg-blue-200';
    if (percentage >= 25) return 'bg-green-200';
    return 'bg-purple-200';  // <25% - purple for very low utilization
  };

  // Format date for display
  const formatDate = (date) => {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  // Header label for the navigation strip. When viewing "This Month" we show the
  // month/year, otherwise the date range.
  const periodLabel = (() => {
    if (!dateRange.start || !dateRange.end) return '';
    if (dateFilter === 'this_month') {
      // Parse YYYY-MM-DD as local — avoids UTC-midnight day-shift in timezones west of UTC.
      const [y, m] = dateRange.start.split('-').map(Number);
      const d = new Date(y, m - 1, 1);
      return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    return `${formatDate(dateRange.start)} - ${formatDate(dateRange.end)}`;
  })();

  const formatShortDate = (date) => {
    const d = new Date(date);
    return `${d.getDate()}`;
  };

  const getDayName = (date) => {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { weekday: 'short' });
  };

  // RBAC Permission Check
  const permissions = user?.permissions?.resourceAllocation || user?.permissions?.projectPlanning || {};
  const canView = permissions?.view ?? true;

  return (
    <Layout user={user}>
      <div className="p-6 space-y-6">
        {/* Header - MD3 Style */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-md3-display-small text-md3-on-surface">Resource Planning - Projects</h1>
            <p className="text-md3-body-medium text-md3-on-surface-variant mt-1">
              Monitor team allocation and availability across all projects
            </p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-md3-surface-container-high rounded-md3-full p-1">
            <TabsTrigger 
              value="summary" 
              className="rounded-md3-full data-[state=active]:bg-md3-primary data-[state=active]:text-md3-on-primary"
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              Summary View
            </TabsTrigger>
            <TabsTrigger 
              value="calendar"
              className="rounded-md3-full data-[state=active]:bg-md3-primary data-[state=active]:text-md3-on-primary"
            >
              <Calendar className="h-4 w-4 mr-2" />
              Calendar View
            </TabsTrigger>
            <TabsTrigger 
              value="board"
              className="rounded-md3-full data-[state=active]:bg-md3-primary data-[state=active]:text-md3-on-primary"
              data-testid="resource-board-tab"
            >
              <Users className="h-4 w-4 mr-2" />
              Drag & Drop Board
            </TabsTrigger>
          </TabsList>

          {/* Summary Tab */}
          <TabsContent value="summary" className="space-y-6 mt-6">
            {/* Date Range Controls - Same as Calendar View */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Select value={dateFilter} onValueChange={setDateFilter}>
                  <SelectTrigger className="w-40 bg-md3-surface-container border-md3-outline rounded-md3-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="this_week">This Week</SelectItem>
                    <SelectItem value="this_month">This Month</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigatePeriod('prev')}
                    className="border-md3-outline rounded-md3-full"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-md3-body-medium text-md3-on-surface px-4">
                    {periodLabel}
                  </span>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigatePeriod('next')}
                    className="border-md3-outline rounded-md3-full"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-md3-label-medium text-md3-on-surface-variant">Total Resources</p>
                      <p className="text-md3-display-small text-md3-on-surface">{summary.total_resources || 0}</p>
                    </div>
                    <div className="h-12 w-12 rounded-md3-full bg-md3-primary-container flex items-center justify-center">
                      <Users className="h-6 w-6 text-md3-on-primary-container" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-md3-label-medium text-md3-on-surface-variant">Over-allocated</p>
                      <p className="text-md3-display-small text-md3-error">{summary.overallocated || 0}</p>
                    </div>
                    <div className="h-12 w-12 rounded-md3-full bg-md3-error-container flex items-center justify-center">
                      <AlertTriangle className="h-6 w-6 text-md3-on-error-container" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-md3-label-medium text-md3-on-surface-variant">Optimal (85-100%)</p>
                      <p className="text-md3-display-small text-md3-tertiary">{summary.optimal || 0}</p>
                    </div>
                    <div className="h-12 w-12 rounded-md3-full bg-md3-tertiary-container flex items-center justify-center">
                      <CheckCircle className="h-6 w-6 text-md3-on-tertiary-container" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-md3-label-medium text-md3-on-surface-variant">Available (&lt;85%)</p>
                      <p className="text-md3-display-small text-md3-primary">{summary.available || 0}</p>
                    </div>
                    <div className="h-12 w-12 rounded-md3-full bg-md3-secondary-container flex items-center justify-center">
                      <Clock className="h-6 w-6 text-md3-on-secondary-container" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-md3-on-surface-variant" />
                <Input
                  placeholder="Search by name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-md3-surface-container border-md3-outline rounded-md3-full"
                />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-48 bg-md3-surface-container border-md3-outline rounded-md3-sm">
                  <Filter className="h-4 w-4 mr-2 text-md3-on-surface-variant" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="overallocated">Over-allocated</SelectItem>
                  <SelectItem value="optimal">Optimal</SelectItem>
                  <SelectItem value="available">Available</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Resource Table */}
            <div className="bg-md3-surface-container rounded-md3-lg shadow-md3-1">
              <div className="p-4 border-b border-md3-outline-variant">
                <h2 className="text-md3-title-large text-md3-on-surface">Team Utilization</h2>
              </div>
              
              {loading ? (
                <div className="text-center py-8 text-md3-on-surface-variant">Loading resources...</div>
              ) : filteredResources.length === 0 ? (
                <div className="text-center py-12">
                  <div className="h-16 w-16 mx-auto mb-4 rounded-md3-full bg-md3-surface-container-high flex items-center justify-center">
                    <BarChart3 className="h-8 w-8 text-md3-on-surface-variant" />
                  </div>
                  <h3 className="text-md3-title-medium text-md3-on-surface mb-2">No Resource Data</h3>
                  <p className="text-md3-body-medium text-md3-on-surface-variant">
                    Resource data will appear here once tasks are assigned in Project Planning
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-md3-outline-variant">
                      <TableHead className="text-md3-label-large text-md3-on-surface-variant">Resource</TableHead>
                      <TableHead className="text-md3-label-large text-md3-on-surface-variant">Allocated</TableHead>
                      <TableHead className="text-md3-label-large text-md3-on-surface-variant">Actual</TableHead>
                      <TableHead className="text-md3-label-large text-md3-on-surface-variant">Capacity</TableHead>
                      <TableHead className="text-md3-label-large text-md3-on-surface-variant">Utilization</TableHead>
                      <TableHead className="text-md3-label-large text-md3-on-surface-variant">Status</TableHead>
                      <TableHead className="text-md3-label-large text-md3-on-surface-variant">Tasks</TableHead>
                      <TableHead className="text-md3-label-large text-md3-on-surface-variant">Projects</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredResources.map((resource, idx) => (
                      <TableRow key={resource.user_id || idx} className="border-md3-outline-variant hover:bg-md3-surface-container-high">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-md3-full bg-md3-primary-container flex items-center justify-center">
                              <span className="text-md3-label-large text-md3-on-primary-container">
                                {resource.user_name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?'}
                              </span>
                            </div>
                            <span className="text-md3-body-medium text-md3-on-surface font-medium">
                              {resource.user_name || 'Unknown'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-md3-body-medium text-md3-on-surface">
                          {resource.allocated_hours?.toFixed(1) || 0} hrs
                        </TableCell>
                        <TableCell className="text-md3-body-medium text-md3-on-surface">
                          {resource.actual_hours?.toFixed(1) || 0} hrs
                        </TableCell>
                        <TableCell className="text-md3-body-medium text-md3-on-surface-variant">
                          {resource.capacity || 40} hrs
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Progress 
                              value={Math.min(resource.utilization_percentage || 0, 100)} 
                              className="w-20"
                            />
                            <span className={`text-md3-body-medium font-medium ${getUtilizationColor(resource.utilization_percentage)}`}>
                              {resource.utilization_percentage?.toFixed(0) || 0}%
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(resource.status)}</TableCell>
                        <TableCell className="text-md3-body-medium text-md3-on-surface-variant">
                          {resource.task_count || resource.activities?.length || 0} tasks
                        </TableCell>
                        <TableCell className="text-md3-body-medium text-md3-on-surface-variant">
                          {resource.project_count || 0}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>

            {/* Task Breakdown */}
            {filteredResources.length > 0 && (
              <Card className="bg-md3-surface-container border-0 shadow-md3-1 rounded-md3-lg">
                <CardHeader>
                  <CardTitle className="text-md3-title-large text-md3-on-surface">Task Breakdown by Resource</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {filteredResources.slice(0, 5).map((resource, idx) => (
                      <div key={resource.user_id || idx} className="border border-md3-outline-variant rounded-md3-lg p-4 bg-md3-surface">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h4 className="text-md3-title-medium text-md3-on-surface">{resource.user_name}</h4>
                            <p className="text-md3-body-small text-md3-on-surface-variant">
                              {resource.allocated_hours?.toFixed(1) || 0}hrs allocated across {resource.project_count || 0} projects
                            </p>
                          </div>
                          {getStatusBadge(resource.status)}
                        </div>
                        {resource.activities?.length > 0 ? (
                          <div className="space-y-2">
                            {resource.activities.slice(0, 5).map((task, taskIdx) => (
                              <div 
                                key={taskIdx}
                                className="flex items-center justify-between p-2 bg-md3-surface-container-high rounded-md3-sm"
                              >
                                <div className="flex-1">
                                  <p className="text-md3-body-medium text-md3-on-surface">
                                    {task.task_description || task.activity_name}
                                  </p>
                                  <p className="text-md3-body-small text-md3-on-surface-variant">
                                    {task.project_number} • {task.activity_name}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge 
                                    className={
                                      task.status === 'done' ? 'bg-md3-tertiary-container text-md3-on-tertiary-container' :
                                      task.status === 'in_progress' ? 'bg-md3-primary-container text-md3-on-primary-container' :
                                      'bg-md3-surface-container text-md3-on-surface-variant'
                                    }
                                  >
                                    {task.status?.replace(/_/g, ' ') || 'todo'}
                                  </Badge>
                                  <span className="text-md3-body-small text-md3-on-surface-variant">
                                    {task.allocated_hours}hrs
                                  </span>
                                </div>
                              </div>
                            ))}
                            {resource.activities.length > 5 && (
                              <p className="text-md3-body-small text-md3-on-surface-variant text-center">
                                +{resource.activities.length - 5} more tasks
                              </p>
                            )}
                          </div>
                        ) : (
                          <p className="text-md3-body-small text-md3-on-surface-variant italic">No tasks assigned</p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Calendar Tab */}
          <TabsContent value="calendar" className="space-y-6 mt-6">
            {/* Date Range Controls */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Select value={dateFilter} onValueChange={setDateFilter}>
                  <SelectTrigger className="w-40 bg-md3-surface-container border-md3-outline rounded-md3-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="this_week">This Week</SelectItem>
                    <SelectItem value="this_month">This Month</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigatePeriod('prev')}
                    className="border-md3-outline rounded-md3-full"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-md3-body-medium text-md3-on-surface px-4">
                    {periodLabel}
                  </span>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigatePeriod('next')}
                    className="border-md3-outline rounded-md3-full"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              {/* Legend */}
              <div className="flex items-center gap-4 text-md3-body-small">
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 bg-purple-200 rounded"></div>
                  <span>&lt;25%</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 bg-green-200 rounded"></div>
                  <span>25-49%</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 bg-blue-200 rounded"></div>
                  <span>50-84%</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 bg-amber-200 rounded"></div>
                  <span>85-100%</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 bg-red-200 rounded"></div>
                  <span>&gt;100%</span>
                </div>
              </div>
            </div>

            {/* Calendar Grid */}
            <div className="bg-md3-surface-container rounded-md3-lg shadow-md3-1 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-md3-outline-variant">
                    <TableHead className="text-md3-label-large text-md3-on-surface-variant sticky left-0 bg-md3-surface-container z-10 min-w-[180px]">
                      Resource
                    </TableHead>
                    {dates.map((date, idx) => {
                      const dateStr = ymd(date);
                      const off = classifyOffDay(date, dateStr);
                      const headerCls = off?.kind === 'holiday'
                        ? 'bg-rose-100 text-rose-900'
                        : off?.kind === 'weekend'
                        ? 'bg-slate-200 text-slate-700'
                        : 'text-md3-on-surface-variant';
                      return (
                        <TableHead
                          key={idx}
                          className={`text-md3-label-medium text-center min-w-[100px] ${headerCls}`}
                          title={off ? `${off.label}` : ''}
                          data-testid={`calendar-header-${dateStr}`}
                        >
                          <div>{getDayName(date)}</div>
                          <div className="text-md3-body-small">{formatShortDate(date)}</div>
                          {off?.kind === 'holiday' && (
                            <div className="text-[10px] mt-0.5 italic truncate" title={off.label}>
                              {off.label}
                            </div>
                          )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {calendarData.length > 0 ? (
                    calendarData.map((resource, idx) => (
                      <TableRow key={resource.user_id || idx} className="border-md3-outline-variant">
                        <TableCell className="sticky left-0 bg-md3-surface-container z-10">
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-md3-full bg-md3-primary-container flex items-center justify-center">
                              <span className="text-md3-label-small text-md3-on-primary-container">
                                {resource.user_name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?'}
                              </span>
                            </div>
                            <span className="text-md3-body-small text-md3-on-surface font-medium">
                              {resource.user_name}
                            </span>
                          </div>
                        </TableCell>
                        {dates.map((date, dateIdx) => {
                          const dateStr = ymd(date);
                          const dayData = resource.days?.[dateStr] || { projects: [], percentage: 0 };
                          const leave = findLeaveForCell(resource.user_id, dateStr);
                          const isApprovedLeave = leave && leave.status === 'approved' && !leave.is_half_day;
                          const off = classifyOffDay(date, dateStr);
                          // Priority: approved leave > holiday > weekend > util heat
                          const cellBg = isApprovedLeave
                            ? 'bg-amber-50'
                            : off?.kind === 'holiday'
                            ? 'bg-rose-50'
                            : off?.kind === 'weekend'
                            ? 'bg-slate-100'
                            : getCellBgColor(dayData.percentage);
                          return (
                            <TableCell
                              key={dateIdx}
                              className={`text-center p-1 ${cellBg}`}
                              title={off ? off.label : ''}
                              data-testid={`calendar-cell-${resource.user_id}-${dateStr}`}
                            >
                              {leave && (
                                <div
                                  className={`mb-1 inline-flex items-center justify-center px-1.5 py-0.5 rounded text-[10px] font-semibold leading-none ${
                                    leave.status === 'approved' && !leave.is_half_day
                                      ? 'bg-amber-200 text-amber-900'
                                      : 'bg-amber-100 text-amber-800 border border-dashed border-amber-300'
                                  }`}
                                  title={`${leave.leave_type || 'Leave'} · ${leave.status}${leave.is_half_day ? ` · half-day ${leave.half_day_period || ''}` : ''}`}
                                  data-testid={`calendar-leave-${resource.user_id}-${dateStr}`}
                                >
                                  {leave.is_half_day ? '½ OOO' : 'ON LEAVE'}
                                </div>
                              )}
                              {dayData.projects?.length > 0 ? (
                                <div className="space-y-1">
                                  {dayData.projects.slice(0, 2).map((proj, pIdx) => (
                                    <div 
                                      key={pIdx}
                                      className="text-xs bg-md3-surface px-1 py-0.5 rounded truncate"
                                      title={`${proj.project_number} - ${proj.hours}hrs`}
                                    >
                                      {proj.project_number}
                                    </div>
                                  ))}
                                  {dayData.projects.length > 2 && (
                                    <div className="text-xs text-md3-on-surface-variant">
                                      +{dayData.projects.length - 2}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                !leave && <span className="text-md3-body-small text-md3-on-surface-variant">-</span>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))
                  ) : resources.length > 0 ? (
                    resources.map((resource, idx) => (
                      <TableRow key={resource.user_id || idx} className="border-md3-outline-variant">
                        <TableCell className="sticky left-0 bg-md3-surface-container z-10">
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-md3-full bg-md3-primary-container flex items-center justify-center">
                              <span className="text-md3-label-small text-md3-on-primary-container">
                                {resource.user_name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?'}
                              </span>
                            </div>
                            <span className="text-md3-body-small text-md3-on-surface font-medium">
                              {resource.user_name}
                            </span>
                          </div>
                        </TableCell>
                        {dates.map((date, dateIdx) => (
                          <TableCell key={dateIdx} className="text-center bg-md3-surface">
                            <span className="text-md3-body-small text-md3-on-surface-variant">-</span>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={dates.length + 1} className="text-center py-8 text-md3-on-surface-variant">
                        No resources with assigned tasks
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Drag & Drop Board Tab */}
          <TabsContent value="board" className="space-y-6 mt-6">
            <ResourceBoard user={user} />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default ResourceAllocation;
